import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import EventBus from "../../EventBus";
import { CookWorkStatusReasoning } from "../../enums/CookWorkStatusReasoning";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { colors } from "../../styles/colors";
import { CookService } from "../../services/CookServices";

const cookService = new CookService();

interface MarkNotFreeForWorkDialogProps {
  cookId: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const formatReasonLabel = (reason: CookWorkStatusReasoning) =>
  reason
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");

const MarkNotFreeForWorkDialog = ({
  cookId,
  open,
  onClose,
  onSuccess,
}: MarkNotFreeForWorkDialogProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const palette = isDarkMode ? colors.dark : colors.light;
  const [cookWorkStatusReasoning, setCookWorkStatusReasoning] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const reasonOptions = useMemo(
    () => Object.values(CookWorkStatusReasoning),
    [],
  );
  const fieldBackground = isDarkMode ? palette.bgInputField : palette.bgWhite;
  const sharedFieldSx = {
    borderRadius: "12px",
    backgroundColor: fieldBackground,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    boxShadow: isDarkMode ? "none" : "0 1px 2px rgba(16, 24, 40, 0.04)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: palette.borderInputField,
      borderWidth: 1,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.primary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.primary,
      borderWidth: 2,
    },
  };
  const fieldLabelSx = {
    mb: 0.1,
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: palette.textSecondary,
  };

  const resetState = () => {
    setCookWorkStatusReasoning("");
    setComment("");
    setShowValidation(false);
  };

  const handleClose = () => {
    if (saving) return;
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    if (!cookId) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Cook details not available",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    if (!cookWorkStatusReasoning) {
      setShowValidation(true);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please select a reason to continue",
        type: CustomSnackbarTypes.WARNING,
      });
      return;
    }

    setSaving(true);
    const payload: {
      cookWorkStatusReasoning: string;
      comment?: string;
    } = {
      cookWorkStatusReasoning,
    };
    if (comment.trim()) {
      payload.comment = comment.trim();
    }

    cookService
      .markNotFreeForWork(cookId, payload)
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Successfully marked not free to work",
          type: CustomSnackbarTypes.SUCCESS,
        });
        resetState();
        onClose();
        onSuccess();
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "16px",
          overflow: "hidden",
          background: isDarkMode
            ? palette.bgWhite
            : "linear-gradient(180deg, #FFFFFF 0%, #FCFDFE 100%)",
          boxShadow: isDarkMode
            ? undefined
            : "0 24px 60px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${palette.borderPrimary}`,
          fontSize: "18px",
          fontWeight: 700,
          color: palette.textBlack,
        }}
      >
        Mark Not Free To Work
        <IconButton
          onClick={handleClose}
          disabled={saving}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            "&:hover": {
              backgroundColor: palette.bgButtonlight,
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 3.75, pb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Reason *</Typography>
            <FormControl
              fullWidth
              error={showValidation && !cookWorkStatusReasoning}
              size="small"
            >
              <InputLabel
                id="mark-not-free-reason-label"
                shrink={false}
                sx={{ display: "none" }}
              >
                Reason
              </InputLabel>
              <Select
                labelId="mark-not-free-reason-label"
                value={cookWorkStatusReasoning}
                input={<OutlinedInput notched={false} />}
                onChange={(e) => {
                  setCookWorkStatusReasoning(e.target.value);
                  if (showValidation) {
                    setShowValidation(false);
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box
                        component="span"
                        sx={{
                          color: palette.textGrey,
                          fontSize: "15px",
                          fontWeight: 400,
                        }}
                      >
                        Select a reason
                      </Box>
                    );
                  }

                  return formatReasonLabel(selected as CookWorkStatusReasoning);
                }}
                sx={{
                  ...sharedFieldSx,
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    minHeight: "52px",
                    boxSizing: "border-box",
                    px: 1.5,
                    py: 1,
                    fontSize: "15px",
                    color: palette.textBlack,
                  },
                }}
              >
                {reasonOptions.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {formatReasonLabel(reason)}
                  </MenuItem>
                ))}
              </Select>
              {showValidation && !cookWorkStatusReasoning && (
                <FormHelperText sx={{ mt: 0.75 }}>
                  Please select a reason
                </FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.3,
              }}
            >
              <Typography sx={fieldLabelSx}>Comment</Typography>
            </Box>
            <TextField
              placeholder="Add supporting context for the team"
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  ...sharedFieldSx,
                  alignItems: "flex-start",
                  minHeight: 124,
                },
                "& .MuiOutlinedInput-inputMultiline": {
                  padding: "14px 16px",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: palette.textBlack,
                },
                "& .MuiOutlinedInput-inputMultiline::placeholder": {
                  color: palette.textGrey,
                  opacity: 1,
                  fontSize: "14px",
                  lineHeight: 1.5,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: `1px solid ${palette.borderPrimary}`,
          gap: 1,
          backgroundColor: isDarkMode ? palette.bgWhite : "rgba(248, 250, 252, 0.72)",
        }}
      >
        <Button
          variant="text"
          onClick={handleClose}
          disabled={saving}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            minWidth: 92,
            height: 40,
            px: 2,
            fontWeight: 600,
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: theme.palette.error.main + "14",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !cookWorkStatusReasoning}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            minWidth: 136,
            height: 40,
            px: 2.5,
            boxShadow: "0 8px 18px rgba(25, 117, 210, 0.24)",
            backgroundColor: colors.primaryblue,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: colors.primaryDark,
              boxShadow: "0 10px 22px rgba(29, 78, 216, 0.28)",
            },
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkNotFreeForWorkDialog;
